import CheckIcon from '@mui/icons-material/Check'
import MinusIcon from '@mui/icons-material/Remove'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import PlusIcon from '../assets/landing/plus-icon.svg?react'
import PremiumIconWhite from '../assets/landing/premium-icon-white.svg?react'
import PremiumIcon from '../assets/landing/premium-icon.svg?react'
import { landingAssetsForLocale } from '../config/landingAssets'

type TableCellValue =
  | { type: 'text'; content: React.ReactNode; color?: 'default' | 'premium' }
  | { type: 'check'; color?: 'default' | 'premium' }
  | { type: 'minus' }
  | { type: 'list'; items: string[]; color?: 'default' | 'premium' }

interface TableFeature {
  name: React.ReactNode
  free: TableCellValue
  premium: TableCellValue
  isFirstRow?: boolean
  isLastRow?: boolean
}

export default function ProductPricingSection({
  highlightNonPremiumPricing = false,
  isPremiumUser,
  premiumDeepLinkUrl,
  logClickedLearnMoreAboutPremium,
}: {
  highlightNonPremiumPricing?: boolean
  isPremiumUser: boolean | null
  premiumDeepLinkUrl: string
  logClickedLearnMoreAboutPremium: () => void
}) {
  const { t, i18n } = useTranslation('landingPage')
  const assets = landingAssetsForLocale(i18n.language)
  const deeplinkAnchorRef = useRef<HTMLAnchorElement>(null)
  const premiumUpsellPricePrefix = t('pricing.premiumUpsell.pricePrefix')
  const premiumUpsellPriceAmount = t('pricing.premiumUpsell.priceAmount')
  const premiumUpsellPriceSuffix = t('pricing.premiumUpsell.priceSuffix')

  const tableFeatures = useMemo<TableFeature[]>(() => {
    const filteringPremiumItems = t('pricing.comparison.rows.filtering.premium').split('\n')

    return [
      {
        name: (
          <span className='whitespace-pre-line'>{t('pricing.comparison.rows.price.name')}</span>
        ),
        free: { type: 'text', content: t('pricing.comparison.rows.price.free') },
        premium: {
          type: 'text',
          content: t('pricing.comparison.rows.price.premium'),
          color: 'premium',
        },
        isFirstRow: true,
      },
      {
        name: (
          <span className='whitespace-pre-line'>
            {t('pricing.comparison.rows.mediaCount.name')}
          </span>
        ),
        free: {
          type: 'text',
          content: (
            <span className='whitespace-pre-line'>
              {t('pricing.comparison.rows.mediaCount.free')}
            </span>
          ),
        },
        premium: {
          type: 'text',
          content: t('pricing.comparison.rows.mediaCount.premium'),
          color: 'premium',
        },
      },
      {
        name: t('pricing.comparison.rows.video.name'),
        free: { type: 'minus' },
        premium: { type: 'check', color: 'premium' },
      },
      {
        name: (
          <span className='whitespace-pre-line'>{t('pricing.comparison.rows.filtering.name')}</span>
        ),
        free: { type: 'minus' },
        premium: {
          type: 'list',
          items: filteringPremiumItems,
          color: 'premium',
        },
      },
      {
        name: t('pricing.comparison.rows.multipleAlbums.name'),
        free: { type: 'check' },
        premium: { type: 'check', color: 'premium' },
      },
      {
        name: (
          <span className='whitespace-pre-line'>
            {t('pricing.comparison.rows.remoteSettings.name')}
          </span>
        ),
        free: { type: 'check' },
        premium: { type: 'check', color: 'premium' },
        isLastRow: true,
      },
    ]
  }, [t])

  const renderCell = useCallback((value: TableCellValue) => {
    switch (value.type) {
      case 'text': {
        const textColor = value.color === 'premium' ? 'text-[#EE5F87]' : 'text-gray-12'
        return <span className={textColor}>{value.content}</span>
      }
      case 'check': {
        const checkColor = value.color === 'premium' ? 'text-[#EE5F87]' : 'text-gray-12'
        return <CheckIcon className={`h-4 w-4 ${checkColor} mx-auto`} />
      }
      case 'minus':
        return <MinusIcon className='text-gray-12 mx-auto h-4 w-4' />
      case 'list':
        return (
          <div className='flex justify-center'>
            <ul
              className={`list-none space-y-1 text-left ${value.color === 'premium' ? 'text-[#EE5F87]' : 'text-gray-12'}`}
            >
              {value.items.map((item, idx) => (
                <li key={idx}>・{item}</li>
              ))}
            </ul>
          </div>
        )
    }
  }, [])

  const getRowClasses = useCallback((feature: TableFeature) => {
    return `bg-white ${feature.isLastRow ? '' : 'border-b border-gray-100'}`
  }, [])

  const getFeatureCellClasses = useCallback((feature: TableFeature) => {
    const base = 'p-4 text-xs text-gray-12 border-r border-gray-100'
    if (feature.isFirstRow) {
      return `${base} rounded-tl-lg`
    }
    if (feature.isLastRow) {
      return `${base} rounded-bl-lg`
    }
    return base
  }, [])

  const getFreeCellClasses = useCallback(() => {
    return 'p-4 text-xs text-gray-12 font-semibold text-center border-r border-gray-100'
  }, [])

  const getPremiumCellClasses = useCallback((feature: TableFeature) => {
    const base = 'p-4 text-sm font-semibold text-center'
    if (feature.isLastRow) {
      return `${base} rounded-br-lg`
    }
    return base
  }, [])

  const onLearnMoreAboutPremiumClick = useCallback(() => {
    logClickedLearnMoreAboutPremium()
    deeplinkAnchorRef.current?.click()
  }, [logClickedLearnMoreAboutPremium])

  return (
    <section className='space-y-6 bg-[#F5F5F5] px-4 py-8'>
      <div className='space-y-4'>
        <h2 className='text-gray-12 mb-6 text-center text-2xl font-semibold'>
          {t('pricing.heading')}
        </h2>
        {isPremiumUser ? (
          <>
            <div className='w-full rounded-lg bg-[#FB6B92] py-2'>
              <div className='whitespace-pre-line text-center text-sm font-semibold text-white'>
                {t('pricing.premiumUser.badge')}
              </div>
            </div>
            <div className='text-gray-12 tracking-0 text-left text-base leading-[160%]'>
              <p className='font-semibold'>{t('pricing.premiumUser.bodyLead')}</p>
              <p className='font-normal'>{t('pricing.premiumUser.note')}</p>
            </div>
          </>
        ) : (
          <div className='text-gray-12 tracking-0 text-left text-base font-normal leading-[160%]'>
            <p>{t('pricing.freeUser.bodyLead')}</p>
            <p>{t('pricing.freeUser.bodySub')}</p>
          </div>
        )}
      </div>

      <div className='flex flex-col gap-4 rounded-lg bg-white p-4'>
        <div>
          <img
            src={assets.priceImg1}
            alt='Digital Photo Frame Product'
            className='w-full rounded-lg'
            loading='lazy'
          />
        </div>

        <div className='text-gray-12 space-y-2'>
          <h3 className='tracking-0 text-xl font-semibold leading-[140%]'>
            {t('pricing.product.title')}
          </h3>
          <p
            className={`tracking-0 text-xl font-semibold leading-[140%] ${
              !isPremiumUser && highlightNonPremiumPricing ? 'text-[#EE5F87]' : 'text-gray-12'
            }`}
          >
            {t('pricing.product.price')}
          </p>
          <div className='tracking-0 space-y-1 text-base font-normal leading-[160%]'>
            <p>{t('pricing.product.packageLabel')}</p>
            <p>{t('pricing.product.packageContent')}</p>
          </div>
        </div>
      </div>

      <PlusIcon className='mx-auto h-8 w-8' />

      {!isPremiumUser && (
        <>
          <div className='space-y-4 text-center text-[#EE5F87]'>
            <div className='flex flex-col gap-6'>
              <div className='flex-1'>
                <p className='whitespace-pre-line text-xl font-semibold'>
                  {t('pricing.premiumUpsell.heading')}
                </p>
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-4 rounded-lg bg-white p-4'>
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <PremiumIcon className='text-gray-12 h-5 w-5 flex-shrink-0' />
                <h4 className='text-gray-12 text-xl font-semibold'>
                  {t('pricing.premiumUpsell.title')}
                </h4>
              </div>
              <p className='text-gray-12 text-xl font-semibold'>
                {premiumUpsellPricePrefix}
                {premiumUpsellPricePrefix && ' '}
                <span className={highlightNonPremiumPricing ? 'text-[#EE5F87]' : undefined}>
                  {premiumUpsellPriceAmount}
                </span>
                {premiumUpsellPriceSuffix && ' '}
                {premiumUpsellPriceSuffix}
              </p>
              <div className='text-gray-12 tracking-0 text-left text-base font-normal leading-[160%]'>
                <p>{t('pricing.premiumUpsell.bodyLead')}</p>
                <p>{t('pricing.premiumUpsell.bodySub')}</p>
              </div>
            </div>
            <a
              ref={deeplinkAnchorRef}
              href={premiumDeepLinkUrl}
              className='hidden'
              aria-hidden='true'
            />
            <button
              className={`mx-auto block rounded-[20px] bg-[#F5F5F5] px-8 py-2 text-sm font-semibold ${
                highlightNonPremiumPricing ? 'text-mi-text-primary' : 'text-blue-10'
              }`}
              onClick={onLearnMoreAboutPremiumClick}
            >
              {t('pricing.premiumUpsell.button')}
            </button>
          </div>
        </>
      )}

      <div className='space-y-6'>
        <h2 className='text-gray-12 text-center text-xl font-semibold'>
          {t('pricing.comparison.heading')}
        </h2>
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse'>
            <thead>
              <tr>
                <th className='w-1/3'></th>
                <th className='w-1/3 rounded-tl-lg bg-[#646464] p-3 text-center text-sm font-semibold text-white'>
                  {t('pricing.comparison.freeColumn')}
                </th>
                <th className='relative w-1/3 rounded-tr-lg bg-[#EE5F87] p-3 text-center text-sm font-bold text-white'>
                  <div className='flex items-center justify-center gap-1'>
                    <PremiumIconWhite className='h-5 w-5' />
                    <span className='text-sm font-semibold'>
                      {t('pricing.comparison.premiumColumn')}
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className='overflow-hidden rounded-lg text-center'>
              {tableFeatures.map((feature, index) => (
                <tr key={index} className={getRowClasses(feature)}>
                  <td className={getFeatureCellClasses(feature)}>{feature.name}</td>
                  <td className={getFreeCellClasses()}>{renderCell(feature.free)}</td>
                  <td className={getPremiumCellClasses(feature)}>{renderCell(feature.premium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className='text-gray-12 text-left text-sm font-normal leading-relaxed'>
          {t('pricing.comparison.note')}
        </p>
      </div>
    </section>
  )
}
